import type { ShareGrid } from './types';

// Spoiler-free 1200×630 share card, JUMP-manga themed (best-practice §7.2):
// brand + mode + #N + colored result grid + "SOLVED IN x/8" + streak. NEVER the answer.
// No external/cover images are drawn, so the canvas stays untainted and exports cleanly.

const W = 1200;
const H = 630;
const INK = '#16130e';
const RED = '#e60012';
const PAPER = '#faf6ec';
const YELLOW = '#f2b705';
const CELL: Record<string, string> = { correct: '#27a567', partial: '#f2b705', wrong: '#d8534f' };

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export interface ShareCard {
  mode: string;
  grid: ShareGrid;
  attempts: number;
  max: number | null;
  won: boolean;
  streak: number;
  day: number;
}

export async function renderShareCanvas({ mode, grid, attempts, max, won, streak, day }: ShareCard): Promise<HTMLCanvasElement> {
  if ('fonts' in document) {
    try {
      await Promise.all([document.fonts.load('700 90px "Bangers"'), document.fonts.load('400 52px "Mochiy Pop One"')]);
    } catch {
      /* ignore */
    }
  }
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d')!;

  // paper + halftone
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(21,18,12,0.12)';
  for (let y = 8; y < H; y += 15) for (let x = 8; x < W; x += 15) { ctx.beginPath(); ctx.arc(x, y, 1.5, 0, 7); ctx.fill(); }

  // outer panel
  ctx.strokeStyle = INK;
  ctx.lineWidth = 10;
  roundRect(ctx, 26, 26, W - 52, H - 52, 12);
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.stroke();

  // masthead bar
  ctx.fillStyle = INK;
  roundRect(ctx, 54, 54, W - 108, 120, 8);
  ctx.fill();
  ctx.textBaseline = 'middle';
  ctx.font = '90px "Bangers", sans-serif';
  ctx.fillStyle = RED;
  ctx.fillText('ANIMEDLE', 86, 120);
  ctx.fillStyle = '#fff';
  ctx.fillText('ANIMEDLE', 82, 116);
  ctx.fillStyle = RED;
  ctx.font = '900 34px Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(`${mode.toUpperCase()} · #${day}`, W - 96, 116);
  ctx.textAlign = 'left';

  // result grid (spoiler-free colors only)
  const gy = 230;
  if (grid.type === 'grid') {
    const cols = grid.rows[0]?.length ?? 6;
    const cw = 64;
    const gap = 12;
    const gridW = cols * cw + (cols - 1) * gap;
    const startX = (W - gridW) / 2;
    grid.rows.slice(0, 6).forEach((row, ri) => {
      row.forEach((state, ci) => {
        ctx.fillStyle = CELL[state];
        roundRect(ctx, startX + ci * (cw + gap), gy + ri * (cw + gap) * 0.62, cw, cw * 0.6, 8);
        ctx.fill();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 4;
        ctx.stroke();
      });
    });
  } else {
    const sq = 52;
    const gap = 12;
    const gridW = Math.min(grid.squares.length, 8) * (sq + gap) - gap;
    const startX = (W - gridW) / 2;
    grid.squares.slice(0, 8).forEach((isAnswer, i) => {
      ctx.fillStyle = isAnswer ? CELL.correct : CELL.wrong;
      roundRect(ctx, startX + i * (sq + gap), gy + 40, sq, sq, 8);
      ctx.fill();
      ctx.strokeStyle = INK;
      ctx.lineWidth = 4;
      ctx.stroke();
    });
  }

  // result line + streak
  ctx.fillStyle = INK;
  ctx.font = '52px "Mochiy Pop One", sans-serif';
  ctx.textAlign = 'center';
  const verdict = won ? `SOLVED IN ${attempts}/${max ?? '∞'}` : `X/${max ?? '∞'}`;
  ctx.fillText(verdict, W / 2, H - 150);

  ctx.font = '900 30px Arial, sans-serif';
  ctx.fillStyle = RED;
  ctx.fillText(`STREAK ${streak}   ·   animedle.today`, W / 2, H - 92);
  ctx.textAlign = 'left';

  // onomatopoeia accent
  ctx.save();
  ctx.translate(W - 250, H - 140);
  ctx.rotate(0.14);
  ctx.font = '70px "Bangers", sans-serif';
  ctx.fillStyle = YELLOW;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4;
  ctx.strokeText('ドン!!', 0, 0);
  ctx.fillText('ドン!!', 0, 0);
  ctx.restore();

  return c;
}

export function canvasToBlob(c: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => c.toBlob(resolve, 'image/png'));
}
